import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "assistant-widget" }
const _hoisted_2 = {
  key: 0,
  class: "assistant-widget__panel",
  role: "dialog",
  "aria-label": "CRM Assistant"
}
const _hoisted_3 = { class: "assistant-widget__modal-frame" }
const _hoisted_4 = {
  key: 0,
  class: "assistant-widget__notice"
}
const _hoisted_5 = ["aria-label"]
const _hoisted_6 = {
  class: "assistant-widget__notice-avatars",
  "aria-hidden": "true"
}
const _hoisted_7 = { class: "assistant-widget__notice-spark" }
const _hoisted_8 = { class: "assistant-widget__notice-text" }
const _hoisted_9 = { class: "assistant-widget__notice-title" }
const _hoisted_10 = { class: "assistant-widget__notice-sub" }
const _hoisted_11 = ["aria-expanded", "aria-label"]
const _hoisted_12 = {
  key: 0,
  class: "assistant-widget__count"
}
const _hoisted_13 = {
  key: 1,
  class: "assistant-widget__unread"
}
const _hoisted_14 = {
  key: 2,
  class: "assistant-widget__label"
}

import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { Icon, Modal } from "@/components/ui";
import type { AgentUnreadReplyDraft } from "@/dtos/Agent.dto";
import AssistantWindow from "@/components/agent/assistant/AssistantWindow.vue";
import ContactAvatar from "@/components/agent/assistant/ContactAvatar.vue";
import { provideAssistantChat } from "@/composables/agent/useAssistantChat";

// The CRM assistant, a bubble in the bottom-right corner of every signed-in page (App.vue).
// It opens as a panel docked to the bubble, or expanded into a centered modal; the rep's
// last choice is remembered. Its chat is owned here, so it carries on across pages.
const EXPANDED_STORAGE_KEY = "assistant-widget-expanded";


export default /*@__PURE__*/_defineComponent({
  __name: 'AssistantWidget',
  setup(__props) {

const {
  messages,
  isSending,
  isLoadingThread,
  now,
  inbox,
  showHistory,
  isVisible,
  conversationId,
  open: openThread,
} = provideAssistantChat();

const savedExpanded = (): boolean => {
  try {
    return localStorage.getItem(EXPANDED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const isOpen = ref(false);
const isExpanded = ref(savedExpanded());
// A reply landed while the window was closed.
const hasUnread = ref(false);

const open = () => {
  isOpen.value = true;
  hasUnread.value = false;
};
const close = () => (isOpen.value = false);
const toggle = () => (isOpen.value ? close() : open());

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
  try {
    localStorage.setItem(EXPANDED_STORAGE_KEY, String(isExpanded.value));
  } catch {
    // Storage blocked: the choice still applies until the page reloads.
  }
};

watch(
  () => messages.value.length,
  (length, previous) => {
    const last = messages.value[length - 1];
    if (!isOpen.value && length > (previous ?? 0) && last?.role !== "user") hasUnread.value = true;
  }
);

// Drafts in the open chat still waiting on the rep, shown on the bubble.
const badgeCount = computed(
  () =>
    messages.value
      .flatMap((m) => m.actions ?? [])
      .filter(
        (a) =>
          a.status === "pending" && (!a.expiresAt || new Date(a.expiresAt).getTime() > now.value)
      ).length + inbox.unread.value.length
);

// ── New reply drafts ────────────────────────────────────────
// Unread AI reply drafts pop up above the bubble as a message from the assistant: one
// contact's reply, or "3 new replies". Hiding it keeps it hidden until another one comes.
const CHANNEL_LABEL = { email: "by email", linkedin: "on LinkedIn" } as const;
const noticeKey = (item: { conversationId: string; actionId: string }) =>
  `${item.conversationId}:${item.actionId}`;
const dismissed = ref(new Set<string>());

const noticeItems = computed(() =>
  inbox.unread.value.filter((item) => !dismissed.value.has(noticeKey(item)))
);
const showNotice = computed(() => !isOpen.value && noticeItems.value.length > 0);

const nameOf = (item: AgentUnreadReplyDraft) => item.contact?.name ?? "A contact";

const noticeTitle = computed(() => {
  const items = noticeItems.value;
  if (items.length !== 1) return `${items.length} new replies`;
  const [item] = items;
  return `${nameOf(item)} replied${item.channel ? ` ${CHANNEL_LABEL[item.channel]}` : ""}`;
});

const noticeSubtitle = computed(() => {
  const items = noticeItems.value;
  if (items.length === 1) {
    const { preview } = items[0];
    return preview ? `"${preview}" · I drafted a reply for you` : "I drafted a reply for you";
  }
  const names = items.slice(0, 2).map(nameOf);
  const others = items.length - names.length;
  return `${names.join(", ")}${others > 0 ? ` and ${others} more` : ""} · I drafted a reply to each`;
});

const dismissNotice = () => {
  dismissed.value = new Set([...dismissed.value, ...noticeItems.value.map(noticeKey)]);
};

/** One reply opens its conversation; several open the list, where each has its own. */
const openReplies = () => {
  const items = noticeItems.value;
  open();
  if (items.length === 1) {
    showHistory.value = false;
    // Re-read even when it's the thread already loaded: the new draft may have come since.
    openThread(items[0].conversationId, { reload: true });
  } else {
    showHistory.value = true;
  }
};

// Opening the window onto a reply thread that got a new draft while it was shut reads it.
// Skipped while a thread is loading (e.g. the notice just opened one): reloading the old
// thread here would cancel that load and land the rep in the wrong conversation.
watch(isOpen, (visible) => {
  isVisible.value = visible;
  const openId = conversationId.value;
  if (
    visible &&
    openId &&
    !isLoadingThread.value &&
    inbox.unread.value.some((item) => item.conversationId === openId)
  ) {
    openThread(openId, { reload: true });
  }
});

// The expanded modal renders in <body>, outside App.vue's v-show, so it would stay over the
// next page (or the dialer). Close it on navigation; the docked panel carries on.
const route = useRoute();
watch(
  () => route.fullPath,
  () => {
    if (isOpen.value && isExpanded.value) close();
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_Transition, { name: "assistant-panel" }, {
      default: _withCtx(() => [
        (isOpen.value && !isExpanded.value)
          ? (_openBlock(), _createElementBlock("section", _hoisted_2, [
              _createVNode(AssistantWindow, {
                expanded: false,
                onClose: close,
                onToggleExpand: toggleExpanded
              })
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_unref(Modal), {
      show: isOpen.value && isExpanded.value,
      size: "xl",
      "dialog-max-width": "1120px",
      "show-close-button": false,
      "body-class": "assistant-widget__modal-body",
      onClose: close
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(AssistantWindow, {
            expanded: true,
            onClose: close,
            onToggleExpand: toggleExpanded
          })
        ])
      ]),
      _: 1
    }, 8, ["show"]),
    _createVNode(_Transition, { name: "assistant-notice" }, {
      default: _withCtx(() => [
        (showNotice.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("button", {
                type: "button",
                class: "assistant-widget__notice-body",
                "aria-label": `${noticeTitle.value}. Open in the CRM Assistant`,
                onClick: openReplies
              }, [
                _createElementVNode("span", _hoisted_6, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(noticeItems.value.slice(0, 3), (item) => {
                    return (_openBlock(), _createBlock(ContactAvatar, {
                      key: item.conversationId,
                      class: "assistant-widget__notice-avatar",
                      name: item.contact?.name ?? 'Contact',
                      src: item.contact?.profilePictureUrl,
                      size: 34
                    }, null, 8, ["name", "src"]))
                  }), 128)),
                  _createElementVNode("span", _hoisted_7, [
                    _createVNode(_unref(Icon), { icon: "mdi:creation" })
                  ])
                ]),
                _createElementVNode("span", _hoisted_8, [
                  _cache[0] || (_cache[0] = _createElementVNode("span", { class: "assistant-widget__notice-from" }, [
                    _createTextVNode(" CRM Assistant "),
                    _createElementVNode("span", { class: "assistant-widget__notice-new" }, "New")
                  ], -1)),
                  _createElementVNode("span", _hoisted_9, _toDisplayString(noticeTitle.value), 1),
                  _createElementVNode("span", _hoisted_10, _toDisplayString(noticeSubtitle.value), 1)
                ])
              ], 8, _hoisted_5),
              _createElementVNode("button", {
                type: "button",
                class: "assistant-widget__notice-close",
                "aria-label": "Hide this notification",
                title: "Hide",
                onClick: dismissNotice
              }, [
                _createVNode(_unref(Icon), { icon: "mdi:close" })
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createElementVNode("button", {
      type: "button",
      class: _normalizeClass(["assistant-widget__bubble", {
        'assistant-widget__bubble--open': isOpen.value,
        'assistant-widget__bubble--busy': _unref(isSending),
      }]),
      "aria-expanded": isOpen.value,
      "aria-label": isOpen.value ? 'Close CRM Assistant' : 'Open CRM Assistant',
      onClick: toggle
    }, [
      _cache[1] || (_cache[1] = _createElementVNode("span", {
        class: "assistant-widget__ring",
        "aria-hidden": "true"
      }, null, -1)),
      _createVNode(_Transition, {
        name: "assistant-icon",
        mode: "out-in"
      }, {
        default: _withCtx(() => [
          (isOpen.value)
            ? (_openBlock(), _createBlock(_unref(Icon), {
                key: "close",
                icon: "mdi:chevron-down",
                class: "assistant-widget__icon"
              }))
            : (_openBlock(), _createBlock(_unref(Icon), {
                key: "open",
                icon: "mdi:creation",
                class: "assistant-widget__icon"
              }))
        ]),
        _: 1
      }),
      (!isOpen.value && badgeCount.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(badgeCount.value > 9 ? "9+" : badgeCount.value), 1))
        : (!isOpen.value && hasUnread.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_13))
          : _createCommentVNode("", true),
      (!isOpen.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_14, "Ask CRM Assistant"))
        : _createCommentVNode("", true)
    ], 10, _hoisted_11)
  ]))
}
}

})